import { ApplicationManager, Component } from '@lisergia/core'
import { Viewport } from '@lisergia/managers'
import { DOMRectBounds, DOMUtils, MathUtils } from '@lisergia/utilities'

import GSAP from 'gsap'
import sample from 'lodash/sample'
import { autorun } from 'mobx'

export default class HeaderSprite extends Component {
  static INDEX = 0

  declare classes: {
    active: string
  }

  declare element: HTMLElement

  index = 0
  length = 0
  sprite = ''
  frameWidthRem = 30.9375 // 495px / 16
  frameHeightRem = 35.25 // 564px / 16

  spriteElement = document.createElement('div')
  currentIndex = 0

  constructor({ application, element }: { application: ApplicationManager; element: HTMLElement }) {
    super({
      application,
      element,
    })

    const [sprite, lengthValue, frameWidth, frameHeight] = this.element.dataset.headerSprite!.split(',')

    this.index = HeaderSprite.INDEX++
    this.length = parseInt(lengthValue)
    this.sprite = sprite

    // Parse frame dimensions from dataset if provided, otherwise use defaults
    if (frameWidth && frameHeight) {
      this.frameWidthRem = parseInt(frameWidth) / 16
      this.frameHeightRem = parseInt(frameHeight) / 16
    } else if (sprite === 'melody') {
      this.frameWidthRem = 598 / 16
      this.frameHeightRem = 630 / 16
    }

    this.setupSprite()

    this.preload().then(() => {
      this.animate()
    })

    autorun(this.onScroll)
  }

  setupSprite() {
    this.spriteElement.style.width = `${this.frameWidthRem}rem`
    this.spriteElement.style.height = `${this.frameHeightRem}rem`
    this.spriteElement.style.backgroundImage = `url(${window.VARIABLES.THEME}/images/${this.sprite}-sprite.png)`
    this.spriteElement.style.backgroundSize = `${this.frameWidthRem * this.length}rem ${this.frameHeightRem}rem`
    this.spriteElement.style.backgroundRepeat = 'no-repeat'
    this.spriteElement.style.backgroundPosition = '0 0'
    this.spriteElement.style.visibility = 'hidden'

    this.element.appendChild(this.spriteElement)
  }

  async preload() {
    return new Promise<void>((resolve) => {
      const img = new Image()
      img.onload = () => resolve()
      img.onerror = () => resolve()
      img.src = `${window.VARIABLES.THEME}/images/${this.sprite}-sprite.png`
    })
  }

  setFrame(index: number) {
    const clampedIndex = Math.max(0, Math.min(Math.round(index), this.length - 1))

    if (clampedIndex === this.currentIndex) return

    this.currentIndex = clampedIndex
    const xPos = -(clampedIndex * this.frameWidthRem)
    this.spriteElement.style.backgroundPosition = `${xPos}rem 0`
  }

  animate() {
    const values = { index: 0 }

    const timeline = GSAP.timeline()

    timeline.fromTo(
      this.spriteElement,
      {
        autoAlpha: 0,
        scale: 0.8,
      },
      {
        autoAlpha: 1,
        duration: 2,
        ease: 'elastic(1, 0.5)',
        scale: 1,
      },
    )

    timeline.fromTo(
      values,
      { index: 0 },
      {
        duration: 1,
        ease: 'linear',
        index: this.length - 1,
        onUpdate: () => {
          this.setFrame(values.index)
        },
      },
      0.2,
    )
  }

  amount: number = 150

  declare rotation: number
  declare speed: number

  declare bounds: DOMRectBounds

  onResize() {
    this.bounds = DOMUtils.getBounds(this.element, this.application?.scroll)

    this.rotation = sample([15, 17.5, 20])
    this.speed = sample([-0.5, -0.65, -0.7])
  }

  onScroll() {
    const scroll = this.application?.currentPage?.scroll ?? 0

    const index = MathUtils.map(scroll, 0, Viewport.height * 0.25, this.length - 1, 0, true)

    this.setFrame(index)

    const y = MathUtils.map(this.bounds.top - scroll, Viewport.height, -Viewport.height, -this.amount, this.amount)

    const rotate = MathUtils.map(
      this.bounds.top - scroll,
      Viewport.height,
      -Viewport.height,
      this.rotation,
      -this.rotation,
    )

    this.spriteElement.style.transform = `translate3d(0, ${y}px, 0) rotate(${rotate}deg)`
  }

  addEventListeners(): void {
    Viewport.on('resize', this.onResize)
  }

  removeEventListeners(): void {
    Viewport.off('resize', this.onResize)
  }
}
